import React, { useEffect, useState } from "react";
import { X, Mail, Loader2, Eye } from "lucide-react";
import { getInvitePreview, inviteAthlete } from "../../lib/api";
import { toast } from "sonner";

/**
 * Modal that lets the coach preview + edit the athlete invite email before
 * sending. Editable fields: subject + plain-text message body. The payment
 * link + install instructions are baked into the template server-side.
 *
 * Props:
 *   athlete: { id, name, email, payment_link, ... }
 *   open: bool
 *   onClose: () => void
 *   onSent: (deliveryInfo) => void   // called after successful send
 */
export default function InviteAthleteModal({ athlete, open, onClose, onSent }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, message, payment_link, html }
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [showHtml, setShowHtml] = useState(false);

  useEffect(() => {
    if (!open || !athlete?.id) return;
    let live = true;
    setLoading(true);
    getInvitePreview(athlete.id)
      .then((p) => {
        if (!live) return;
        setPreview(p);
        setSubject(p.subject || "");
        setMessage(p.message || "");
      })
      .catch(() => {
        if (!live) return;
        toast.error("Couldn't load invite preview");
        onClose && onClose();
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [open, athlete?.id, onClose]);

  if (!open) return null;

  const paymentLink = preview?.payment_link || "";

  const send = async () => {
    if (!subject.trim()) {
      toast.error("Subject can't be empty");
      return;
    }
    setSending(true);
    try {
      const res = await inviteAthlete(athlete.id, {
        subject: subject.trim(),
        message: message,
      });
      if (res.delivery === "email") {
        toast.success(`Invite emailed to ${athlete.email}`);
      } else {
        try {
          await navigator.clipboard.writeText(res.invite_url);
          toast.success("Invite URL copied to clipboard — share with athlete");
        } catch {
          toast(`Invite URL: ${res.invite_url}`, { duration: 15000 });
        }
      }
      onSent && onSent(res);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invite");
    } finally {
      setSending(false);
    }
  };

  // Live HTML preview: re-render with the coach's current edits by rebuilding
  // the message HTML client-side. For MVP we just show the server-provided HTML
  // (which uses the *default* copy) toggled off by default — coaches usually
  // read the plain-text and trust the wrapper. Toggle exposes the full HTML.
  const displayHtml = preview?.html || "";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="invite-athlete-modal"
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-2xl border border-[var(--tir-border)]">
        <div className="sticky top-0 z-10 bg-white border-b border-[var(--tir-border)] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-burgundy)]">
              Athlete Invite
            </div>
            <div className="font-serif-display text-2xl text-[var(--tir-ink)] mt-0.5">
              {athlete?.name}
            </div>
            <div className="text-xs text-[var(--tir-muted)] font-mono-tir mt-0.5">
              {athlete?.email}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-modal-close"
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-[var(--tir-muted)]">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading preview…
            </div>
          ) : (
            <>
              {/* Payment link status */}
              <div className="bg-[var(--tir-ivory)] border border-[var(--tir-border)] rounded-md p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                  Payment link included
                </div>
                {paymentLink ? (
                  <div
                    className="text-xs font-mono-tir text-[var(--tir-navy)] break-all"
                    data-testid="invite-modal-payment-link"
                  >
                    {paymentLink}
                  </div>
                ) : (
                  <div className="text-xs text-[var(--tir-muted)] italic">
                    None &mdash; set a default in Coach settings or add one on the athlete&rsquo;s profile.
                  </div>
                )}
                <div className="text-[10px] tracking-wide text-[var(--tir-muted)] mt-1.5">
                  To change per-athlete: close this and edit the athlete profile. To change the studio-wide default: use &ldquo;Default payment link&rdquo; on the coaching hub.
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                  Subject
                </label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="invite-modal-subject"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                  Personal message
                  <span className="ml-2 text-[10px] tracking-normal text-[var(--tir-muted)]/80 normal-case font-normal">
                    (appears above the Set-Password button)
                  </span>
                </label>
                <textarea
                  rows={8}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="invite-modal-message"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none leading-relaxed resize-y font-mono-tir"
                />
                <div className="text-[10px] text-[var(--tir-muted)] mt-1">
                  Plain text. Double-newline for a paragraph break. Payment link + install instructions are added automatically below your message.
                </div>
              </div>

              {/* HTML preview */}
              <div className="border border-[var(--tir-border)] rounded-md">
                <button
                  type="button"
                  onClick={() => setShowHtml((v) => !v)}
                  data-testid="invite-modal-toggle-html"
                  className="w-full flex items-center justify-between px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:bg-[var(--tir-ivory)]"
                >
                  <span className="inline-flex items-center gap-2">
                    <Eye size={13} /> {showHtml ? "Hide" : "Show"} full email preview
                  </span>
                  <span className="text-[var(--tir-muted)] normal-case tracking-normal font-normal">
                    (renders default copy — your edits go live on send)
                  </span>
                </button>
                {showHtml && (
                  <iframe
                    title="invite preview"
                    srcDoc={displayHtml}
                    className="w-full h-[520px] border-t border-[var(--tir-border)] bg-white"
                    data-testid="invite-modal-html-iframe"
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 bg-white border-t border-[var(--tir-border)] px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            data-testid="invite-modal-cancel"
            className="text-xs tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={send}
            disabled={loading || sending || !athlete?.email}
            data-testid="invite-modal-send"
            className="inline-flex items-center gap-2 border border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[#5e1521] disabled:opacity-60 transition-colors"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            {sending ? "Sending…" : "Send invite"}
          </button>
        </div>
      </div>
    </div>
  );
}
